import React, { useEffect, useId, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { filterArticlesForSearch } from '../../data/articles';

const MAX_ITEMS = 8;

export function ArticleSearchCombobox({
  searchQuery,
  setSearchQuery,
  onPickArticle,
  onEmptySearch,
  onNoMatchesSearch,
  inputClass,
  placeholder,
  formClassName = '',
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const matches = useMemo(() => filterArticlesForSearch(searchQuery), [searchQuery]);
  const shown = useMemo(() => matches.slice(0, MAX_ITEMS), [matches]);

  useEffect(() => {
    setHighlight(0);
  }, [searchQuery]);

  const scheduleClose = () => {
    window.setTimeout(() => setOpen(false), 200);
  };

  const pick = (path) => {
    setOpen(false);
    onPickArticle(path);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) {
      onEmptySearch();
      return;
    }
    if (shown.length === 0) {
      onNoMatchesSearch();
      return;
    }
    pick(shown[Math.min(highlight, shown.length - 1)].path);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
      return;
    }
    if (!open || shown.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
  };

  const showList = open && Boolean(searchQuery.trim());

  return (
    <form className={formClassName} onSubmit={handleSubmit} role="search" aria-label="Search articles">
      <div className="relative">
        <input
          type="search"
          autoComplete="off"
          aria-expanded={showList}
          aria-controls={showList ? listId : undefined}
          aria-activedescendant={
            showList && shown.length > 0 ? `${listId}-option-${highlight}` : undefined
          }
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={scheduleClose}
          onKeyDown={handleInputKeyDown}
          className={inputClass}
          aria-label="Search articles"
        />
        <button
          type="submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-800"
          aria-label="Search"
        >
          <Search size={17} strokeWidth={2} />
        </button>

        {showList ? (
          <ul
            id={listId}
            role="listbox"
            className="absolute left-0 right-0 z-[120] mt-1 max-h-72 overflow-y-auto rounded-md border border-brand-100 bg-white py-1 shadow-lg ring-1 ring-slate-900/10"
          >
            {shown.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-500" role="presentation">
                No matching articles
              </li>
            ) : (
              <>
                {shown.map((a, i) => (
                  <li key={a.id} id={`${listId}-option-${i}`} role="option" aria-selected={i === highlight}>
                    <button
                      type="button"
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors ${
                        i === highlight
                          ? 'bg-brand-50 text-brand-900'
                          : 'text-slate-800 hover:bg-slate-50'
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(a.path)}
                    >
                      <span className="font-medium leading-snug">{a.title}</span>
                      <span className="text-xs text-slate-500">{a.author}</span>
                    </button>
                  </li>
                ))}
                {matches.length > MAX_ITEMS ? (
                  <li
                    className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500"
                    role="presentation"
                  >
                    {matches.length} matches — type more to narrow the list.
                  </li>
                ) : null}
              </>
            )}
          </ul>
        ) : null}
      </div>
    </form>
  );
}
